<template>
  <div class="auth">
    <div class="card auth__card">
      <h1 class="auth__title">{{ t("invite.title") }}</h1>
      <p class="auth__lede muted small">{{ t("invite.lede") }}</p>

      <div v-if="!token" class="notice notice--danger">
        <div class="notice__body">{{ t("invite.missingToken") }}</div>
      </div>

      <template v-else>
        <div v-if="error" class="notice notice--danger auth__error">
          <div class="notice__body">{{ error }}</div>
        </div>

        <form class="stack" @submit.prevent="submit">
          <div class="field">
            <label class="field__label" for="name">{{ t("auth.yourName") }}</label>
            <input id="name" v-model.trim="name" class="input" autocomplete="name" maxlength="120" required />
          </div>
          <div class="field">
            <label class="field__label" for="password">{{ t("auth.password") }}</label>
            <input id="password" v-model="password" class="input" type="password" autocomplete="new-password" minlength="12" required />
            <span class="field__hint">{{ t("auth.passwordHint") }}</span>
          </div>
          <div class="field">
            <label class="field__label" for="confirm">{{ t("invite.confirm") }}</label>
            <input id="confirm" v-model="confirm" class="input" type="password" autocomplete="new-password" required
                   :class="{ 'input--invalid': mismatch }" />
            <span v-if="mismatch" class="field__error">{{ t("invite.mismatch") }}</span>
          </div>
          <button class="btn btn--block" type="submit" :disabled="busy || mismatch">
            <span v-if="busy" class="spinner" aria-hidden="true"></span>
            {{ t("invite.accept") }}
          </button>
        </form>
      </template>

      <p class="auth__foot small muted">
        {{ t("auth.haveAccount") }}
        <router-link to="/sign-in">{{ t("nav.signIn") }}</router-link>
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { api } from "../lib/api";
import { signInWithPassword } from "../lib/session";
import { useErrorText } from "../lib/errors";

const { t } = useI18n();
const errorText = useErrorText();
const route = useRoute();
const router = useRouter();

const token = ref(typeof route.query.token === "string" ? route.query.token : "");
const name = ref("");
const password = ref("");
const confirm = ref("");
const busy = ref(false);
const error = ref("");

const mismatch = computed(() => !!confirm.value && confirm.value !== password.value);

// Drop the invite token from the address bar and history once it is in memory.
onMounted(() => {
  if (route.query.token) router.replace({ name: "accept-invite" });
});

async function submit() {
  busy.value = true;
  error.value = "";
  try {
    const result = (await api.acceptInvite({ token: token.value, password: password.value, name: name.value })) || {};
    const email = result.email || result.user?.email || "";
    const accepted = t("invite.accepted");
    if (email) {
      const outcome = await signInWithPassword(email, password.value);
      if (outcome === "signed_in") {
        router.push({ name: "apps" });
        return;
      }
    }
    router.push({ name: "sign-in", state: { email, notice: accepted } });
  } catch (e) {
    error.value = errorText(e);
  } finally {
    busy.value = false;
  }
}
</script>
